/**
 * Cruise streaks (M3 plan R10, spec §10): `count` instanced line segments recycled inside a sphere around the
 * camera. Every segment lies along the flight direction, stretched to clamp(speed × `lengthPerSpeedS`, min, max),
 * and flows backwards past the camera; its alpha rises with speed and fades towards the sphere's edge. Positions
 * are camera-relative (render space: the mesh sits at the render origin, D-011), kept in float64 on the CPU and
 * uploaded as float32 each frame.
 *
 * The quads are built in view space: the long axis is the flight direction, the short axis is perpendicular to it
 * and to the view ray, so the streaks radiate from the vanishing point when you fly straight at it.
 * `update` runs per frame and allocates nothing (D-014).
 */
import {
  AdditiveBlending,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  Vector3,
} from 'three/webgpu'
import { attribute, cameraProjectionMatrix, cameraViewMatrix, cross, float, length, normalize, positionGeometry, select, smoothstep, uniform, uv, vec3, vec4 } from 'three/tsl'
import { createRng, nextFloat, type Rng } from '../../core/rng'
import { linearRgb, setLinearRgb } from './color'
import type { StreaksConfig } from './config'

/** The per-instance attribute holding each segment's centre (vec3, render space). */
export const STREAK_POS_ATTR = 'aStreakPos'

export interface CruiseStreaks {
  /** Place at the render origin with an identity transform; hide it when not cruising. */
  readonly root: Mesh
  /** Segment centres relative to the camera (m), xyz per segment. */
  readonly positions: Float64Array
  readonly count: number
  /** The streak length and alpha the last `update` used. */
  readonly length: number
  readonly alpha: number
  /** `speed` in m/s; (dirX, dirY, dirZ) the unit flight direction in world axes (the render axes, D-011). */
  update(dt: number, speed: number, dirX: number, dirY: number, dirZ: number): void
  /** Re-reads the colour, width and intensity (dev-panel tuning). */
  refresh(): void
  dispose(): void
}

/** The streak length (m) at `speed` (m/s): clamp(speed × lengthPerSpeedS, minLengthM, maxLengthM). */
export function streakLength(speed: number, cfg: StreaksConfig): number {
  const l = (speed > 0 ? speed : 0) * cfg.lengthPerSpeedS
  if (l < cfg.minLengthM) return cfg.minLengthM
  return l < cfg.maxLengthM ? l : cfg.maxLengthM
}

/** The streak alpha at `speed`: an eased rise from `alphaSpeedMin` to `alphaSpeedMax`, up to `maxAlpha`. */
export function streakAlpha(speed: number, cfg: StreaksConfig): number {
  const t = (speed - cfg.alphaSpeedMin) / (cfg.alphaSpeedMax - cfg.alphaSpeedMin)
  const c = t > 0 ? (t < 1 ? t : 1) : 0
  return c * c * (3 - 2 * c) * cfg.maxAlpha
}

/**
 * Writes segment `i` to a uniform random point in the ball of `radius`. With a non-zero direction the point is
 * reflected into the forward half, so it flows back through the whole sphere.
 */
export function respawnStreak(pos: Float64Array, i: number, rng: Rng, radius: number, dirX: number, dirY: number, dirZ: number): void {
  const z = 2 * nextFloat(rng) - 1
  const phi = 2 * Math.PI * nextFloat(rng)
  const r = radius * Math.cbrt(nextFloat(rng))
  const s = Math.sqrt(1 - z * z)
  let x = r * s * Math.cos(phi)
  let y = r * s * Math.sin(phi)
  let w = r * z
  const d = x * dirX + y * dirY + w * dirZ
  if (d < 0) {
    x -= 2 * d * dirX
    y -= 2 * d * dirY
    w -= 2 * d * dirZ
  }
  const o = i * 3
  pos[o] = x
  pos[o + 1] = y
  pos[o + 2] = w
}

/**
 * Moves every segment backwards by `flow` metres along the direction and respawns any whose extent
 * (centre distance + half its length) leaves the sphere. Allocation-free.
 */
export function stepStreaks(
  pos: Float64Array,
  count: number,
  rng: Rng,
  flow: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  len: number,
  cfg: StreaksConfig,
): number {
  const keep = cfg.sphereRadiusM - len / 2
  const spawn = cfg.sphereRadiusM - cfg.maxLengthM / 2
  const dx = dirX * flow
  const dy = dirY * flow
  const dz = dirZ * flow
  let respawned = 0
  for (let i = 0; i < count; i++) {
    const o = i * 3
    const x = pos[o] - dx
    const y = pos[o + 1] - dy
    const z = pos[o + 2] - dz
    if (x * x + y * y + z * z > keep * keep) {
      respawnStreak(pos, i, rng, spawn, dirX, dirY, dirZ)
      respawned++
    } else {
      pos[o] = x
      pos[o + 1] = y
      pos[o + 2] = z
    }
  }
  return respawned
}

export function createCruiseStreaks(cfg: StreaksConfig): CruiseStreaks {
  const count = cfg.count
  const rng = createRng(cfg.seed)
  const positions = new Float64Array(count * 3)
  const spawnRadius = cfg.sphereRadiusM - cfg.maxLengthM / 2
  for (let i = 0; i < count; i++) respawnStreak(positions, i, rng, spawnRadius, 0, 0, 0)

  const base = new PlaneGeometry(1, 1)
  const geometry = new InstancedBufferGeometry()
  geometry.index = base.index
  geometry.setAttribute('position', base.getAttribute('position'))
  geometry.setAttribute('uv', base.getAttribute('uv'))
  const posArray = new Float32Array(count * 3)
  const posAttr = new InstancedBufferAttribute(posArray, 3)
  posAttr.setUsage(DynamicDrawUsage)
  geometry.setAttribute(STREAK_POS_ATTR, posAttr)
  geometry.instanceCount = count

  const uDir = uniform(new Vector3(0, 0, -1))
  const uLength = uniform(cfg.minLengthM)
  const uWidth = uniform(cfg.widthM)
  const uAlpha = uniform(0)
  const uRadius = uniform(cfg.sphereRadiusM)
  const uEdgeFade = uniform(cfg.edgeFade)
  const uColor = uniform(linearRgb(cfg.color))
  const uIntensity = uniform(cfg.intensity)

  const aPos = attribute(STREAK_POS_ATTR, 'vec3')
  const quad = positionGeometry.xy
  const viewPos = cameraViewMatrix.mul(vec4(aPos, 1)).xyz
  const dirView = cameraViewMatrix.mul(vec4(uDir, 0)).xyz
  const side0 = cross(dirView, viewPos)
  const side = select(length(side0).greaterThan(1e-6), normalize(side0), vec3(1, 0, 0))
  const corner = viewPos.add(dirView.mul(quad.x.mul(uLength))).add(side.mul(quad.y.mul(uWidth)))

  const material = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  material.name = 'reachFx:streaks'
  material.vertexNode = cameraProjectionMatrix.mul(vec4(corner, 1))
  const edge = smoothstep(float(1).sub(uEdgeFade), float(1), length(aPos).div(uRadius)).oneMinus()
  // Segments flow backwards (−dir): the bright head trails a tail towards +dir; soft across the width.
  const along = uv().x.oneMinus()
  const across = uv().y.sub(0.5).abs().mul(2).oneMinus()
  material.colorNode = uColor.mul(uIntensity)
  material.opacityNode = uAlpha.mul(edge).mul(along).mul(across)

  const root = new Mesh(geometry, material)
  root.name = 'reachFx:streaks'
  root.frustumCulled = false
  root.visible = false

  let len = cfg.minLengthM
  let alpha = 0

  function upload(): void {
    for (let i = 0; i < posArray.length; i++) posArray[i] = positions[i]
    posAttr.needsUpdate = true
  }
  upload()

  return {
    root,
    positions,
    count,
    get length(): number {
      return len
    },
    get alpha(): number {
      return alpha
    },
    update(dt: number, speed: number, dirX: number, dirY: number, dirZ: number): void {
      len = streakLength(speed, cfg)
      alpha = streakAlpha(speed, cfg)
      const v = speed < cfg.maxFlowSpeed ? speed : cfg.maxFlowSpeed
      const flow = dt > 0 && v > 0 ? v * dt : 0
      stepStreaks(positions, count, rng, flow, dirX, dirY, dirZ, len, cfg)
      uDir.value.set(dirX, dirY, dirZ)
      uLength.value = len
      uAlpha.value = alpha
      upload()
    },
    refresh(): void {
      uWidth.value = cfg.widthM
      uEdgeFade.value = cfg.edgeFade
      setLinearRgb(uColor.value, cfg.color)
      uIntensity.value = cfg.intensity
    },
    dispose(): void {
      root.removeFromParent()
      geometry.dispose()
      base.dispose()
      material.dispose()
    },
  }
}
